import { useState } from "react";
import { useIntl } from "react-intl";
import { IForm } from "../../";
import { lastSaveConflict } from "../../../../utils/lastSaveConflict";
import VehicleChildForm from "./components/vehicleChildForm";
import { getRequiredFields } from "./utils/required-fields-by-step";
import DealershipChildForm from "./components/dealershipChildForm";
import ServiceChildForm from "./components/serviceChildForm";
import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import SuccessDialog from "../../../../components/dialogs/SuccessDialog";
import {
  OccurrenceStepEnum,
  OccurrenceStepLabelIdMap,
} from "../../../../enums/occurrenceStep";
import { formatFieldLabel } from "./utils/fields-labels";
import { Occurrence } from "../../../../models/occurrence";
import { FinalizeOccurrenceButton } from "../../dialogs/finalizationReasonDialog";

const AnalysisWarrantyForm = (props: IForm) => {
  const intl = useIntl();
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
  const [changeStepErrorMessage, setChangeStepErrorMessage] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  const requiredServiceFields = getRequiredFields(
    props.occurrence?.occurrenceType === "P" ? "ANALYSIS_WARRANTY_PREVENTIVE_CHECKUP" : "ANALYSIS_WARRANTY",
    props.occurrence?.occurrenceType
  );

  return (
    <div>
      <div className="text-size-h4 font-bold">{intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.title" })}</div>
      <div className="text-color-status-resting text-size-14">
        {intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.subtitle" })}
      </div>

      <VehicleChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={["assetId", "protocolNumber"]}
        disabledFields={["vehicleChassis"]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      <DealershipChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[
          "dealershipId",
          "dealershipUniqueId",
          "dealershipLocation",
          "dealershipRepresentative",
          "dealershipRegional",
          "dealershipPhone",
          "dealershipDemarcatedArea",
        ]}
        disabledFields={[]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <ServiceChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[
          "timeOpenProtocol",
          "country",
          "driverCheckInDriver",
          "mechanicLocation",
          "towTruckLocation",
          "vehiclePayloadType",
          "vehicleMaximumPayload",
          "vehicleCriticalPayload",
          "partOrderStatus",
          "checklist",
          "solutionProposed",
        ]}
        disabledFields={[
          "estimateTimeRepair",
          "criticality",
          "createdBy",
          "occurrenceType",
          "source",
          "modules",
          "hasLink",
          "mainOccurrence",
          "literatureDiagnosis",
          "literatureTroubleshooting",
          "tasNumber",
          "tasStatus",
          "serviceBaySchedule",
        ]}
        disabledAllFields={props.disabledAllFields}
        requiredFields={requiredServiceFields}
        step={props.step}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        partners={props.partners}
        hasError={hasError}
        persona={props.persona}
        scheduling={props.scheduling}
      />

      <hr className="my-6" />

      {props.persona !== "MANAGER" &&
        !props.finalized &&
        !props.hiddenSaveButton && (
          <div className="flex justify-end">
            <button
              className={`btn btn-primary ${
                props.loading ? "btn-loading disabled" : ""
              }`}
              type="button"
              onClick={async () => {
                if (!props.occurrence) return;
                const isValid = props.isValidStep(requiredServiceFields, props.occurrence);
                setHasError(!isValid);
                if (!isValid) {
                  setShowValidationErrorDialog(true);
                  return;
                }
                const success = await props.save(props.occurrence);
                if (success) {
                  setShowSuccessDialog(true);
                } else if (!lastSaveConflict.current) {
                  setShowErrorDialog(true);
                }
              }}
              disabled={props.loading || props.disabledAllFields}
            >
              {intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.save" })}
            </button>

            <FinalizeOccurrenceButton
              occurrence={props.occurrence}
              finishOccurrence={props.finishOccurrence}
              disabledAllFields={props.disabledAllFields}
              stepId={props.step?.id}
            />
          </div>
        )}

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.saveErrorTitle" })}
        body={intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.saveErrorBody" })}
      />

      <ErrorDialog
        show={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.partNotFilledTitle" })}
        body={intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.partNotFilledBody" })}
      />

      <ErrorDialog
        show={!!changeStepErrorMessage}
        onClose={() => setChangeStepErrorMessage(null)}
        title={intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepTitle" })}
        body={changeStepErrorMessage ?? ""}
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.successTitle" })}
        body={
          <div className="space-y-4">
            <div>{intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.savedSuccess" })}</div>
            {!hasError && (
              <div>
                {intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.advanceQuestion" }, { step: intl.formatMessage({ id: OccurrenceStepLabelIdMap.CUSTOMER_OK }) })}
              </div>
            )}
            {hasError && (
              <>
                <div>
                  {intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.requiredToMove" })}
                </div>
                <div>{intl.formatMessage({ id: "intl-msg:occurrence.step.requiredFields" })}</div>
                <ul className="list-disc list-inside">
                  {props
                    .getNotFilledRequiredFields(
                      requiredServiceFields,
                      props.occurrence ?? ({} as Occurrence)
                    )
                    .map((field: string) => (
                      <li key={field}>
                        {formatFieldLabel(intl, field)}
                      </li>
                    ))}
                </ul>
              </>
            )}
          </div>
        }
        footer={
          <button
            className="btn btn-primary"
            disabled={hasError || !props.changeStep || !props.occurrence}
            onClick={async () => {
              if (!props.summaryStep?.id || !props.occurrence) return;
              try {
                await props.changeStep!(
                  props.occurrence!,
                  props.summaryStep.id,
                  OccurrenceStepEnum.CUSTOMER_OK
                );
                setShowSuccessDialog(false);
                props.onClose();
              } catch (error: any) {
                setShowSuccessDialog(false);
                setChangeStepErrorMessage(error?.message ?? intl.formatMessage({ id: "intl-msg:kanban.form.analysisWarrantyForm.changeStepErrorFallback" }));
              }
            }}
          >
            {intl.formatMessage({ id: "intl-msg:occurrence.step.advanceStepTitle" })}
          </button>
        }
      />
    </div>
  );
};

export default AnalysisWarrantyForm;
